import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { User, UserRole } from '../../types/entities';
import { userService, CreateUserInput, UpdateUserInput } from '../../services/userService';
import toast from 'react-hot-toast';

interface UserFormModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  user: User | null;
}

const UserFormModal: React.FC<UserFormModalProps> = ({
  open,
  onClose,
  onSuccess,
  user,
}) => {
  const { t } = useTranslation();
  const isEdit = !!user;

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    role: 'DRIVER' as UserRole,
    driverId: undefined as number | undefined,
  });

  const [drivers, setDrivers] = useState<Array<{ id: number; name: string }>>([]);
  const [loading, setLoading] = useState(false);
  const [loadingDrivers, setLoadingDrivers] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      setFormData({
        username: user.username,
        email: user.email || '',
        password: '',
        role: user.role,
        driverId: user.driverId,
      });
    } else {
      setFormData({
        username: '',
        email: '',
        password: '',
        role: 'DRIVER',
        driverId: undefined,
      });
    }
  }, [user]);

  useEffect(() => {
    if (open) {
      loadDrivers();
    }
  }, [open]);

  const loadDrivers = async () => {
    try {
      setLoadingDrivers(true);
      const data = await userService.getAvailableDrivers();
      // If editing and user has a driver, include it in the list
      if (user?.driver) {
        const existingDriver = data.find(d => d.id === user.driver!.id);
        if (!existingDriver) {
          data.push(user.driver);
        }
      }
      setDrivers(data);
    } catch (err) {
      console.error('Error loading drivers:', err);
    } finally {
      setLoadingDrivers(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!formData.username.trim()) {
      setError(t('users.error.usernameRequired'));
      return;
    }

    if (!isEdit && !formData.password.trim()) {
      setError(t('users.error.passwordRequired'));
      return;
    }

    if (!isEdit && formData.password.length < 6) {
      setError(t('users.error.passwordTooShort'));
      return;
    }

    try {
      setLoading(true);

      if (isEdit) {
        const updateData: UpdateUserInput = {
          username: formData.username,
          email: formData.email || undefined,
          role: formData.role,
          driverId: formData.driverId || undefined,
        };
        await userService.update(user.id, updateData);
        toast.success(t('common.saved'));
      } else {
        const createData: CreateUserInput = {
          username: formData.username,
          email: formData.email || undefined,
          password: formData.password,
          role: formData.role,
          driverId: formData.driverId,
        };
        await userService.create(createData);
        toast.success(t('common.saved'));
      }

      onSuccess();
    } catch (err: any) {
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError(t('users.error.saveFailed'));
      }
      console.error('Error saving user:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-800">
            {isEdit ? t('users.editUser') : t('users.addUser')}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          {/* Username */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('users.username')} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              required
              autoFocus
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('users.email')}
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Password (only for create) */}
          {!isEdit && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('users.password')} <span className="text-red-500">*</span>
              </label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              />
              <p className="mt-1 text-sm text-gray-500">{t('users.passwordHelp')}</p>
            </div>
          )}

          {/* Role */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('users.role')} <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value as UserRole })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="ADMIN">{t('roles.ADMIN')}</option>
              <option value="OPERATIONS_MANAGER">{t('roles.OPERATIONS_MANAGER')}</option>
              <option value="ACCOUNTANT">{t('roles.ACCOUNTANT')}</option>
              <option value="DRIVER">{t('roles.DRIVER')}</option>
            </select>
          </div>

          {/* Linked Driver */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('users.linkedDriver')}
            </label>
            <select
              value={formData.driverId || ''}
              onChange={(e) => setFormData({ ...formData, driverId: e.target.value ? parseInt(e.target.value) : undefined })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              disabled={loadingDrivers}
            >
              <option value="">{t('common.none')}</option>
              {drivers.map((driver) => (
                <option key={driver.id} value={driver.id}>
                  {driver.name}
                </option>
              ))}
            </select>
          </div>
        </form>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition"
            disabled={loading}
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition disabled:opacity-50"
            disabled={loading}
          >
            {loading ? t('common.processing') : (isEdit ? t('common.update') : t('common.create'))}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserFormModal;